import React from 'react';
import styled from 'styled-components';
import PropTypes from 'prop-types';
import { LocalizedLink } from '@repo/gatsby-localization-helpers';
import { getResizedProductImage } from '@repo/utils';
import { useUiComponentsInternationalContext } from '@repo/static-translations';

import styles from '../styles';
import breakpoints from '../breakpoints';
import PageWrapper from '../page-wrapper';
import GridCard from '../GridCard';
import GridCardContainer from '../GridCardContainer';
import ProductCardGrid from '../ProductCardGrid';
import ProductCard from '../product-card';
import BlockContent from '../sanity/BlockContent';
import BlockContentImage from '../sanity/types/BlockBuilderImage';

const Heading = styled.h1`
  font-weight: revert;
  color: inherit;
  font-size: revert;
  line-height: 1.15;
  margin: 0 0 0.25em;
`;

const BlogImageContainer = styled.div`
  text-align: center;
  margin-bottom: ${styles.spacing['spacing-md']};

  img {
    max-width: 100%;
    max-height: revert;
    aspect-ratio: 3 / 2;
    height: auto;
    object-fit: cover;

    @media (min-width: ${breakpoints.xl}) {
      aspect-ratio: 2 / 1;
    }
  }
`;

const BlogPost = styled.div`
  padding: 0;
  margin: 0 auto;

  @media (min-width: ${breakpoints.md}) {
    padding: 0;
    max-width: 75ch;
  }

  img {
    max-width: 100%;
  }

  p {
    color: ${({ theme }) => theme.colors.defaultFont};
    margin-bottom: 25px;
  }

  ul,
  ol {
    color: ${({ theme }) => theme.colors.defaultFont};
  }

  iframe {
    width: 100%;
    aspect-ratio: 16 / 9;
    border: medium none;
  }
`;

const BlogPostBottom = styled.div`
  display: flex;
  margin-top: ${styles.spacing['spacing-md']};
  flex-direction: column;

  @media (min-width: ${breakpoints.md}) {
    flex-direction: row;
  }
`;

const TagsContainer = styled.div`
  a {
    color: ${({ theme }) => theme.colors.brandPrimary};
  }

  @media (min-width: ${breakpoints.md}) {
    text-align: left;
  }
`;

const BlogTagContainer = styled.div`
  font-weight: ${styles.fontWeight.regular};
  margin-bottom: ${styles.spacing['spacing-md']};
  display: inline-block;
  padding: 0;
  margin-right: ${styles.spacing['spacing-sm']};

  a {
    padding: ${styles.spacing['spacing-sm']} ${styles.spacing['spacing-md']};
    font-weight: ${styles.fontWeight.bold};
    color: ${({ theme }) => theme.colors.linkHover};
    background: ${({ theme }) => theme.colors.tagBackground};
    display: inline-block;
    text-transform: uppercase;
    text-decoration: none;
    font-size: ${styles.fontSize.lg};

    &:hover {
      background-color: ${({ theme }) => theme.colors.tagBackgroundHover};
      color: ${({ theme }) => theme.colors.brandPrimary};
    }
  }
`;

const AuthorWrapper = styled.div`
  padding: revert;
  margin-top: ${styles.spacing['spacing-1xl']};
  display: flex;
  flex-direction: column;
  @media screen and (min-width: ${breakpoints.md}) {
    flex-direction: row;
  }
`;

const AuthorBio = styled.div`
  @media (min-width: ${breakpoints.md}) {
    margin-left: 1.625rem;
  }

  p {
    color: ${({ theme }) => theme.colors.defaultFont};
  }
`;

const AuthorImage = styled.div`
  margin: revert;
`;

const AuthorTitle = styled.h2`
  font-size: revert;
  font-weight: revert;
  color: inherit;
  text-transform: uppercase;
  margin: 0 0 0.25em;
`;

const AuthorLocation = styled.p`
  font-size: ${styles.fontSize.lg};
  color: ${({ theme }) => theme.colors.defaultFont};
  font-weight: ${styles.fontWeight.bold};
  margin-top: 0;
`;

const AuthorLink = styled(LocalizedLink)`
  color: ${({ theme }) => theme.colors.linkHover};
  text-decoration: underline;
  text-transform: uppercase;

  &:hover {
    text-decoration: none;
  }
`;

const CategoryFilter = ({ categories, blogBaseUrl }) => {
  const { tt } = useUiComponentsInternationalContext();

  return (
    categories &&
    categories.map(category => {
      const ariaLabel = tt(s => s.articleComponent.articlesTaggedAriaLabel, {
        title: category?.title,
      });
      const categoryLink = `/${blogBaseUrl}/${category?.slug?.current}`;
      return (
        <BlogTagContainer key={category?.slug?.current}>
          <LocalizedLink to={categoryLink} aria-label={ariaLabel}>
            {category?.title}
          </LocalizedLink>
        </BlogTagContainer>
      );
    })
  );
};

function BlogArticle({ article, blogBaseUrl, authorLink, getInternalLink }) {
  const {
    title,
    body,
    image,
    author,
    categories,
    relatedArticles,
    relatedProducts,
  } = article;

  const { tt } = useUiComponentsInternationalContext();

  const authorParam = author?.title ? (
    <LocalizedLink to={authorLink}>{author.title}</LocalizedLink>
  ) : (
    tt(s => s.articleComponent.theSportDogStaff)
  );

  return (
    <PageWrapper>
      {image?.asset && (
        <BlogImageContainer>
          <BlockContentImage value={image} />
        </BlogImageContainer>
      )}
      <BlogPost>
        <div>
          <Heading>{title}</Heading>
          {tt(s => s.articleComponent.postedBySubTitle, {
            author: authorParam,
          })}
        </div>
        <BlockContent blocks={body} getInternalLink={getInternalLink} />
        <BlogPostBottom>
          <TagsContainer>
            <CategoryFilter categories={categories} blogBaseUrl={blogBaseUrl} />
          </TagsContainer>
        </BlogPostBottom>
      </BlogPost>

      {author?.title && (
        <AuthorWrapper>
          {author?.image && (
            <AuthorImage>
              <BlockContentImage value={author.image} />
            </AuthorImage>
          )}
          <AuthorBio>
            <AuthorTitle>{author.title}</AuthorTitle>
            <AuthorLocation>{author.location}</AuthorLocation>
            <p>{author.description}</p>
            <p>
              <AuthorLink to={authorLink}>
                {tt(s => s.articleComponent.moreAboutThisAuthorLink)}
              </AuthorLink>
            </p>
          </AuthorBio>
        </AuthorWrapper>
      )}

      {relatedProducts?.length > 0 && (
        <>
          <h2>{tt(s => s.articleComponent.relatedProductsHeader)}</h2>
          <ProductCardGrid padding="0">
            {relatedProducts.map(relatedProduct => {
              const store = relatedProduct?.store;
              if (store) {
                store.id = store?.shopifyId;
                return (
                  <ProductCard
                    key={store.title}
                    product={{
                      ...relatedProduct,
                      ...store,
                      image: {
                        src: getResizedProductImage(store?.previewImageUrl),
                        altText: store.title,
                      },
                      sku: store.variants[0]?.sku,
                      price: store.variants[0]?.price,
                      features: relatedProduct?.features,
                    }}
                    titleColor="white"
                    titleHoverColor={({ theme }) => theme.colors.link}
                    priceColor="white"
                    backgroundColor={({ theme }) => theme.colors.cardBackground}
                  />
                );
              }
              return null;
            })}
          </ProductCardGrid>
        </>
      )}

      {relatedArticles?.length > 0 && (
        <>
          <h2>{tt(s => s.articleComponent.relatedArticlesHeader)}</h2>
          <GridCardContainer>
            {relatedArticles.map(relatedArticle => (
              <GridCard
                key={relatedArticle.title}
                item={{
                  ...relatedArticle,
                  subtitle: tt(s => s.articleComponent.relatedArticleSubtitle, {
                    author: relatedArticle?.author?.title
                      ? relatedArticle.author.title
                      : tt(s => s.articleComponent.theSportDogStaff),
                  }),
                  link: `/hunting-training-tips/${relatedArticle.slug.current}`,
                }}
              />
            ))}
          </GridCardContainer>
        </>
      )}
    </PageWrapper>
  );
}

BlogArticle.propTypes = {
  article: PropTypes.shape({
    title: PropTypes.string,
    body: PropTypes.arrayOf(PropTypes.shape({})),
    image: PropTypes.shape({
      asset: PropTypes.shape({}),
    }),
    author: PropTypes.shape({
      title: PropTypes.string,
      description: PropTypes.string,
      location: PropTypes.string,
      image: PropTypes.shape({
        altText: PropTypes.string,
        asset: PropTypes.shape({
          url: PropTypes.string,
          altText: PropTypes.string,
        }),
      }),
    }),
    tags: PropTypes.arrayOf(PropTypes.string),
    categories: PropTypes.arrayOf(
      PropTypes.shape({
        title: PropTypes.string,
        slug: PropTypes.shape({
          current: PropTypes.string,
        }),
      })
    ),
    relatedProducts: PropTypes.arrayOf(PropTypes.shape({})),
    relatedArticles: PropTypes.arrayOf(PropTypes.shape({})),
  }).isRequired,
  blogBaseUrl: PropTypes.string.isRequired,
  authorLink: PropTypes.string.isRequired,
  getInternalLink: PropTypes.func.isRequired,
};

export default BlogArticle;
